import axios, { AxiosInstance } from 'axios'
import { AuthStore } from '../AuthStore'

import { Paginated } from './data/pagination';
import SignInFormData from './data/SignInFormData'
import RecRegistryAccount, { RecRegistryAccountRequest } from './data/RecRegistryAccount'
import RecRegistryCertificateGeneration, { RecRegistryCertificateGenerationRequest } from './data/RecRegistryCertificateGeneration'
import RecRegistryCertificateBundle, { RecRegistryCertificateBundleRequest } from './data/RecRegistryCertificateBundle'
import RecRegistryAction, { RecRegistryActionRequest } from './data/RecRegistryAction'
import RecRegistryBundleSnapshot, { RecRegistryBundleSnapshotRequest } from './data/RecRegistryBundleSnapshot'
import RecRegistryAccreditationCode from './data/RecRegistryAccreditationCode'
import qs from 'qs'; // Query String
import { DateTime } from "luxon";

// Energy API

class EnergyApi {
  client: AxiosInstance
  authStore: AuthStore

  constructor(authStore: AuthStore) {
    this.authStore = authStore
    this.client = axios.create({
      baseURL: '/api/',
      responseType: 'json',
      withCredentials: true
    });

    // response interceptor to sign out if receiving an unauthorized response
    this.client.interceptors.response.use(
      (response) => {
        const expiry = DateTime.fromISO(response.headers["access-token-expires"])
        if (expiry) {
          this.authStore.setExpiresAt(expiry)
        }
        return response
      },
      async (error) => {
        if (error.response.status === 401) {
          this.authStore.signOut();
        }
        return error.response;
      },
    );
  }

  async signIn(formData: SignInFormData): Promise<boolean> {
    const res = await this.client.post('/auth', {}, {
      auth: formData,
    });
    if (res.status === 200) {
      const expiry = DateTime.fromISO(res.headers["access-token-expires"]) ?? DateTime.now()
      this.authStore.signIn(res.data.userName, res.data.userEmail, expiry, res.data.isAdmin, res.data.isSuper)
      return true
    }
    this.authStore.signOut()
    return false
  }

  async signOut(): Promise<void> {
    const res = await this.client.delete('/auth');
    if (res.status === 200) {
      this.authStore.signOut();
    }
  }

  async getRecRegistryAccounts(request: RecRegistryAccountRequest): Promise<Paginated<RecRegistryAccount>> {
    const res = await this.client.get('/rec-registry/accounts', {
      params: request,
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'indices' })
      }
    });
    return Paginated.fromResponse<RecRegistryAccount>(res.data, RecRegistryAccount)
  }

  async getRecRegistryCertificateGenerations(request: RecRegistryCertificateGenerationRequest): Promise<Paginated<RecRegistryCertificateGeneration>> {
    const res = await this.client.get<Paginated<RecRegistryCertificateGeneration>>('/rec-registry/certificate-generations', {
      params: request,
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'indices' })
      }
    });
    const result = Paginated.fromResponse(res.data, RecRegistryCertificateGeneration)
    return result
  }

  async getRecRegistryCertificateGeneration(id: string): Promise<RecRegistryCertificateGeneration | undefined> {
    const res = await this.client.get(`/rec-registry/certificate-generations/${id}`);
    if (res.status === 200) {
      return new RecRegistryCertificateGeneration(res.data)
    }
    return undefined
  }

  async getRecRegistryCertificateBundles(request: RecRegistryCertificateBundleRequest): Promise<Paginated<RecRegistryCertificateBundle>> {
    const res = await this.client.get<Paginated<RecRegistryCertificateBundle>>(request.path, {
      params: request,
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'indices' })
      }
    });
    const result = Paginated.fromResponse(res.data, RecRegistryCertificateBundle)
    return result
  }

  async getRecRegistryActions(request: RecRegistryActionRequest): Promise<Paginated<RecRegistryAction>> {
    const res = await this.client.get<Paginated<RecRegistryAction>>('/rec-registry/actions', {
      params: request,
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'indices' })
      }
    });
    const result = Paginated.fromResponse(res.data, RecRegistryAction)
    return result
  }

  async getAccreditationCode(code: string): Promise<RecRegistryAccreditationCode | undefined> {
    const res = await this.client.get(`/rec-registry/accreditation-codes/${code}`);
    if (res.status === 200) {
      return new RecRegistryAccreditationCode(res.data)
    }
    return undefined
  }

  async getRecRegistryBundleSnapshots(request: RecRegistryBundleSnapshotRequest): Promise<Paginated<RecRegistryBundleSnapshot>> {
    const res = await this.client.get<Paginated<RecRegistryBundleSnapshot>>(request.path, {
      params: request,
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'indices' })
      }
    });
    const result = Paginated.fromResponse(res.data, RecRegistryBundleSnapshot)
    return result
  }
}

export default EnergyApi